// WHAT: Batch securities enrichment UI component with OpenFIGI API integration
// WHY:  Provides user interface for enriching securities data with reference data, metrics, and historical prices
// DOMAIN: Investments → Parqet → Securities Management
// DEPENDENCIES: OpenFIGI API, Supabase sync service, React state management
// RISKS:
//   - NOTE: OpenFIGI API has rate limits - batch processing may fail for large ISIN lists
//   - NOTE: Supabase sync operation is asynchronous - may cause data inconsistency if interrupted
//   - WARN: API key is stored in component state - not persisted securely
//   - NOTE: Error handling for partial enrichment failures may be incomplete

import React, { useState } from "react";

import { Card } from "@/components/ui";

import {
  enrichSecuritiesOpenfigi,
  syncSecuritiesToSupabase,
  type EnrichmentOptions,
  type SecuritiesEnrichmentResult,
} from "../services/api";

interface SecuritiesEnrichmentProperties {
  className?: string;
}

/**
 * WHAT:
 *   Batch securities enrichment UI component with OpenFIGI API integration and Supabase sync.
 *
 * WHY:
 *   Provides user interface for enriching securities data with reference data, metrics, and historical prices.
 *
 * PROPS:
 *   - className?: string — Optional CSS classes for styling
 *
 * BEHAVIOR:
 *   - Accepts ISINs input (comma, semicolon, or newline separated)
 *   - Supports optional OpenFIGI API key for rate limit increases
 *   - Configurable enrichment options (reference data, metrics, prices, dividends)
 *   - Shows progress messages during enrichment process
 *   - Displays enrichment results with success/failure counts
 *   - Optionally syncs enriched data to Supabase
 *   - Handles errors and displays error messages
 *
 * RISKS:
 *   - NOTE: OpenFIGI API has rate limits - batch processing may fail for large ISIN lists
 *   - NOTE: Supabase sync operation is asynchronous - may cause data inconsistency if interrupted
 *   - WARN: API key is stored in component state - not persisted securely
 *   - NOTE: Error handling for partial enrichment failures may be incomplete
 *   - WARN: No validation for ISIN format before processing
 *
 * @param root0
 *
 * @param root0.className
 *
 * @returns {JSX.Element} The SecuritiesEnrichment component
 *
 */
export const SecuritiesEnrichment: React.FC<SecuritiesEnrichmentProperties> = ({ className }) => {
  const [isins, setIsins] = useState<string>("");
  const [apiKey, setApiKey] = useState("");
  const [includeReferenceData, setIncludeReferenceData] = useState(true);
  const [includeMetrics, setIncludeMetrics] = useState(true);
  const [includePriceCache, setIncludePriceCache] = useState(false);
  const [priceCacheDays, setPriceCacheDays] = useState(30);
  const [includeDividends, setIncludeDividends] = useState(false);
  const [dividendLimit, setDividendLimit] = useState(10);
  const [syncToSupabase, setSyncToSupabase] = useState(true);

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string>("");
  const [results, setResults] = useState<null | SecuritiesEnrichmentResult>(null);
  const [error, setError] = useState<string | undefined>();

  /**
   * WHAT:
   *   Handles enrichment process for entered ISINs with configurable options.
   *
   * WHY:
   *   Centralizes enrichment logic with progress tracking and error handling.
   *
   * SIDE EFFECTS:
   *   - Parses ISIN input into array
   *   - Calls OpenFIGI API for enrichment
   *   - Optionally syncs to Supabase
   *   - Updates progress and results state
   *   - Sets error state on failure
   *
   * RISKS:
   *   - NOTE: ISIN parsing uses regex - may not handle all edge cases
   *   - WARN: No rate limiting - may hit API limits with large batches
   *   - NOTE: Supabase sync happens after enrichment - may fail if enrichment succeeds but sync fails
   */
  const handleEnrich = async () => {
    setLoading(true);
    setProgress("Starting enrichment...");
    setError(undefined);
    setResults(null);

    try {
      // NOTE: Parse ISINs from input - supports multiple separators (newline, comma, semicolon)
      // This allows flexible input formats for user convenience
      const isinList = isins
        .split(/[\n,;]/) // NOTE: Regex matches newline, comma, or semicolon
        .map((index) => index.trim()) // NOTE: Remove whitespace from each ISIN
        .filter((index) => index.length > 0); // NOTE: Remove empty strings

      if (isinList.length === 0) {
        throw new Error("Please enter at least one ISIN");
      }

      setProgress(`Enriching ${String(isinList.length)} ISINs...`);

      // NOTE: Build enrichment options object with all user-selected preferences
      const options: EnrichmentOptions = {
        ...(apiKey && { apiKey }), // NOTE: Only include API key if provided
        dividendScheduleLimit: dividendLimit,
        includeDividendSchedule: includeDividends,
        includeMetrics,
        includePriceCache,
        includeReferenceData,
        isins: isinList,
        priceCacheDays,
      };

      const enrichResult = await enrichSecuritiesOpenfigi(options);

      setProgress(
        `Enrichment completed: ${String(enrichResult.erfolgreich)} successful, ${String(enrichResult.fehlgeschlagen)} failed`,
      );
      setResults(enrichResult);

      // NOTE: Sync to Supabase only if enabled and at least one enrichment succeeded
      // WARN: Partial failures are not handled - if some ISINs fail, successful ones still sync
      if (syncToSupabase && enrichResult.erfolgreich > 0) {
        setProgress("Syncing to Supabase...");

        // NOTE: Pass enrichment result and user preferences to sync function
        // This ensures only requested data types are synced
        const syncStats = await syncSecuritiesToSupabase(
          enrichResult,
          includeMetrics,
          includePriceCache,
          includeDividends,
        );

        setProgress(
          `Sync completed: ${String(syncStats.securities_inserted)} securities, ${String(syncStats.metrics_inserted)} metrics`,
        );
      }
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : "Enrichment failed";
      setError(message);
      setProgress("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={className}>
      <Card>
        <div className="p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Securities Enrichment</h2>

          {/* ISIN Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              ISINs (one per line, or comma-separated)
            </label>
            <textarea
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 font-mono text-sm"
              disabled={loading}
              onChange={(e) => {
                setIsins(e.target.value);
              }}
              placeholder="US0378331005&#10;DE0005140008&#10;IE00B4L5Y983"
              rows={6}
              value={isins}
            />
          </div>

          {/* API Key (Optional) */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              OpenFIGI API Key (optional, recommended for &gt;25 ISINs)
            </label>
            <input
              className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              disabled={loading}
              onChange={(e) => {
                setApiKey(e.target.value);
              }}
              placeholder="Enter OpenFIGI API key..."
              type="password"
              value={apiKey}
            />
          </div>

          {/* Enrichment Options */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-3">Enrichment Options</h3>

            <div className="space-y-3">
              <label className="flex items-center space-x-3 text-gray-300">
                <input
                  checked={includeReferenceData}
                  className="w-4 h-4"
                  disabled={loading}
                  onChange={(e) => {
                    setIncludeReferenceData(e.target.checked);
                  }}
                  type="checkbox"
                />
                <span>Include Reference Data (sector, industry, description, etc.)</span>
              </label>

              <label className="flex items-center space-x-3 text-gray-300">
                <input
                  checked={includeMetrics}
                  className="w-4 h-4"
                  disabled={loading}
                  onChange={(e) => {
                    setIncludeMetrics(e.target.checked);
                  }}
                  type="checkbox"
                />
                <span>Include Metrics (price, market cap, PE ratio, etc.)</span>
              </label>

              <div>
                <label className="flex items-center space-x-3 text-gray-300 mb-2">
                  <input
                    checked={includePriceCache}
                    className="w-4 h-4"
                    disabled={loading}
                    onChange={(e) => {
                      setIncludePriceCache(e.target.checked);
                    }}
                    type="checkbox"
                  />
                  <span>Include Historical Prices</span>
                </label>
                {includePriceCache ? (
                  <div className="ml-7">
                    <input
                      className="w-24 px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                      disabled={loading}
                      max="365"
                      min="1"
                      onChange={(e) => {
                        setPriceCacheDays(Number.parseInt(e.target.value) || 30);
                      }}
                      type="number"
                      value={priceCacheDays}
                    />
                    <span className="ml-2 text-gray-400 text-sm">days</span>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="flex items-center space-x-3 text-gray-300 mb-2">
                  <input
                    checked={includeDividends}
                    className="w-4 h-4"
                    disabled={loading}
                    onChange={(e) => {
                      setIncludeDividends(e.target.checked);
                    }}
                    type="checkbox"
                  />
                  <span>Include Dividend Schedule</span>
                </label>
                {includeDividends ? (
                  <div className="ml-7">
                    <input
                      className="w-24 px-3 py-1 bg-gray-800 border border-gray-700 rounded text-white text-sm"
                      disabled={loading}
                      max="50"
                      min="1"
                      onChange={(e) => {
                        setDividendLimit(Number.parseInt(e.target.value) || 10);
                      }}
                      type="number"
                      value={dividendLimit}
                    />
                    <span className="ml-2 text-gray-400 text-sm">entries</span>
                  </div>
                ) : null}
              </div>

              <label className="flex items-center space-x-3 text-gray-300">
                <input
                  checked={syncToSupabase}
                  className="w-4 h-4"
                  disabled={loading}
                  onChange={(e) => {
                    setSyncToSupabase(e.target.checked);
                  }}
                  type="checkbox"
                />
                <span>Sync to Supabase after enrichment</span>
              </label>
            </div>
          </div>

          {/* Action Button */}
          <button
            className={`w-full py-3 rounded font-semibold transition ${
              loading || !isins.trim()
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
            disabled={loading || !isins.trim()}
            onClick={() => {
              void handleEnrich();
            }}
          >
            {loading ? "Enriching..." : "Start Enrichment"}
          </button>

          {/* Progress */}
          {progress ? (
            <div className="mt-6 p-4 bg-blue-900/30 border border-blue-700 rounded">
              <div className="text-blue-300">{progress}</div>
            </div>
          ) : null}

          {/* Error */}
          {error ? (
            <div className="mt-6 p-4 bg-red-900/30 border border-red-700 rounded">
              <div className="text-red-300">{error}</div>
            </div>
          ) : null}

          {/* Results */}
          {results ? (
            <div className="mt-6 p-4 bg-green-900/30 border border-green-700 rounded">
              <h3 className="text-lg font-semibold text-green-300 mb-3">Enrichment Results</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-400">Total</div>
                  <div className="text-2xl font-bold text-white">{results.anzahl}</div>
                </div>
                <div>
                  <div className="text-gray-400">Successful</div>
                  <div className="text-2xl font-bold text-green-400">{results.erfolgreich}</div>
                </div>
                <div>
                  <div className="text-gray-400">Failed</div>
                  <div className="text-2xl font-bold text-red-400">{results.fehlgeschlagen}</div>
                </div>
                <div>
                  <div className="text-gray-400">Complete Data</div>
                  <div className="text-2xl font-bold text-blue-400">
                    {results.reference_data_complete || 0}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
};
